import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { isSome } from '../../util'

import {
  normalizePurchaseOrderLength,
  normalizePurchaseOrderNumber,
  normalizePurchaseOrderProjectName,
  normalizePurchaseOrderSize,
  normalizePurchaseOrderSupplierName,
} from './normalizeOrderLineMatching'
import { purchaseOrderCsvColumnMapper } from './orderImportTypes'
import type { OrderImportRecord } from './orderImportTypes'

dayjs.extend(customParseFormat)

const integerLimit = 2_147_483_647
const optionalText = (value: string | number | null | undefined) => {
  const text = isSome(value) ? String(value).trim() : ''
  return text === '' ? null : text
}

function positiveNumber(value: string | null, round: boolean): number | null {
  if (!isSome(value)) {
    return null
  }
  const text = value.normalize('NFKC').replace(/,/g, '')
  if (!/^\d+(?:\.\d+)?$/.test(text)) {
    return null
  }
  const number = round ? Math.round(Number(text)) : Number(text)
  return Number.isInteger(number) && number > 0 && number <= integerLimit ? number : null
}

// 画面とサーバーで受ける形式が食い違うと「画面は空欄なのに保存は通る」が起きるため、解釈はここに一本化する
export function normalizeOrderDate(value: string): string | null {
  const parsed = dayjs(value.trim(), ['YYYY/MM/DD', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

function optionalDate(value: string | null): Date | null {
  if (!isSome(value)) {
    return null
  }
  const normalized = normalizeOrderDate(value)
  return isSome(normalized) ? new Date(`${normalized}T00:00:00.000Z`) : null
}

export type PurchaseOrderCsvFieldKey = keyof typeof purchaseOrderCsvColumnMapper
export type PurchaseOrderCsvFieldError = { key: PurchaseOrderCsvFieldKey; message: string }

export function validateAndNormalizePurchaseOrderCsvRecords({
  record,
  index,
}: {
  record: OrderImportRecord
  index: number
}) {
  const text = (key: PurchaseOrderCsvFieldKey) => optionalText(record[purchaseOrderCsvColumnMapper[key]])
  const requiredKeys = [
    'projectName',
    'supplierName',
    'orderNumber',
    'sourceFileName',
    'steelType',
    'sectionType',
    'size',
  ] as const
  // message は従来どおり全文を連結してサーバー・既存呼び出しと互換に保ち、
  // 画面が項目ごとに赤枠を出せるよう、同じエラーを項目キー付きでも持つ
  const fieldErrors: PurchaseOrderCsvFieldError[] = []
  const addError = (keys: PurchaseOrderCsvFieldKey[], message: string) => {
    for (const key of keys) {
      fieldErrors.push({ key, message })
    }
  }
  const errors: string[] = []
  const addMessage = (keys: PurchaseOrderCsvFieldKey[], message: string) => {
    errors.push(message)
    addError(keys, message)
  }
  for (const key of requiredKeys) {
    if (!isSome(text(key))) {
      addMessage([key], `${purchaseOrderCsvColumnMapper[key]}は必須です。`)
    }
  }
  const quantity = positiveNumber(text('quantity'), false)
  const weight = positiveNumber(text('weight'), true)
  const lineNo = positiveNumber(text('lineNo'), false)
  const lengthText = text('length')
  const lengthValue = isSome(lengthText) ? normalizePurchaseOrderLength(lengthText) : null
  const length = isSome(lengthValue) && lengthValue <= integerLimit ? lengthValue : null
  const orderedAt = optionalDate(text('orderedAt'))
  const deliveryDueAt = optionalDate(text('deliveryDueAt'))
  const numericResults = { quantity, weight, lineNo, length }
  for (const key of ['quantity', 'weight', 'lineNo', 'length'] as const) {
    if (isSome(text(key)) && !isSome(numericResults[key])) {
      addMessage([key], `${purchaseOrderCsvColumnMapper[key]}は正の数値で入力してください。`)
    }
  }
  if (!isSome(quantity) && !isSome(weight)) {
    addMessage(['quantity', 'weight'], '本数と重量のどちらかは必須です。')
  }
  if (isSome(text('orderedAt')) && !isSome(orderedAt)) {
    addMessage(['orderedAt'], '発注日はYYYY/MM/DDまたはYYYY-MM-DDで入力してください。')
  }
  if (isSome(text('deliveryDueAt')) && !isSome(deliveryDueAt)) {
    addMessage(['deliveryDueAt'], '納期はYYYY/MM/DDまたはYYYY-MM-DDで入力してください。')
  }
  const projectName = text('projectName') ?? ''
  const supplierName = text('supplierName') ?? ''
  const orderNumber = text('orderNumber') ?? ''
  const projectNameNormalized = normalizePurchaseOrderProjectName(projectName)
  const supplierNameNormalized = normalizePurchaseOrderSupplierName(supplierName)
  const orderNumberNormalized = normalizePurchaseOrderNumber(orderNumber)
  const size = text('size') ?? ''
  const sizeNormalized = normalizePurchaseOrderSize(size)
  const emptyNormalizedKeys = (
    [
      ['projectName', projectNameNormalized],
      ['supplierName', supplierNameNormalized],
      ['orderNumber', orderNumberNormalized],
      ['size', sizeNormalized],
    ] as const
  ).flatMap(([key, value]) => (value === '' ? [key] : []))
  if (emptyNormalizedKeys.length > 0) {
    addMessage([...emptyNormalizedKeys], '物件名・仕入先・発注No・寸法は正規化後も値が必要です。')
  }
  if (errors.length > 0) {
    return { success: false as const, index, record, message: errors.join(' '), fieldErrors }
  }
  return {
    success: true as const,
    index,
    record,
    result: {
      projectName,
      projectNameNormalized,
      supplierName,
      supplierNameNormalized,
      orderNumber,
      orderNumberNormalized,
      // 原本名は完全一致キーなので、空欄検証以外で前後空白を取り除かない。
      sourceFileName: String(record[purchaseOrderCsvColumnMapper.sourceFileName] ?? ''),
      lineNo,
      steelType: text('steelType') ?? '',
      sectionType: text('sectionType') ?? '',
      size,
      sizeNormalized,
      length,
      quantity,
      weight,
      orderedAt,
      deliveryDueAt,
      deliveryPlace: text('deliveryPlace'),
      plateNumber: text('plateNumber'),
      buildingPart: text('buildingPart'),
      constructionArea: text('constructionArea'),
      remarks: text('remarks'),
      raw: record,
    },
  }
}

export type NormalizedPurchaseOrderImportRow = Extract<
  ReturnType<typeof validateAndNormalizePurchaseOrderCsvRecords>,
  { success: true }
>['result']
